/**
 * TechnicianList Component
 *
 * Sortable list of technicians with expandable rows for daily breakdown.
 * Uses teal color theme to distinguish from Service Advisor (orange).
 */

import React, { useState, useCallback } from 'react';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  BarsArrowDownIcon,
} from '@heroicons/react/24/outline';
import { Technician, TechDailyEntry, SortConfig, SortField } from './types';
import { TechnicianRow } from './TechnicianRow';

interface TechnicianListProps {
  technicians: Technician[];
  dailyByTech: Record<string, TechDailyEntry[]>;
  sortConfig: SortConfig;
  onSortChange: (config: SortConfig) => void;
}

const SORT_COLUMNS: Array<{
  field: SortField;
  label: string;
  shortLabel: string;
}> = [
  { field: 'name', label: 'Technician', shortLabel: 'Name' },
  { field: 'flatRateHoursTotal', label: 'Flat Rate Hrs', shortLabel: 'FRH' },
  { field: 'laborGrossTotal', label: 'Labor Gross', shortLabel: 'Gross' },
  { field: 'elrTotal', label: 'ELR', shortLabel: 'ELR' },
];

export const TechnicianList: React.FC<TechnicianListProps> = ({
  technicians,
  dailyByTech,
  sortConfig,
  onSortChange,
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpanded = useCallback((techId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(techId)) {
        next.delete(techId);
      } else {
        next.add(techId);
      }
      return next;
    });
  }, []);

  const handleSort = useCallback(
    (field: SortField) => {
      onSortChange({
        field,
        direction:
          sortConfig.field === field && sortConfig.direction === 'desc'
            ? 'asc'
            : 'desc',
      });
    },
    [sortConfig, onSortChange]
  );

  const renderSortIndicator = (field: SortField) => {
    if (sortConfig.field !== field) {
      return <BarsArrowDownIcon className="h-4 w-4 text-gray-300" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ChevronUpIcon className="h-4 w-4 text-teal-600" />
    ) : (
      <ChevronDownIcon className="h-4 w-4 text-teal-600" />
    );
  };

  if (technicians.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No technician data available for this period.
      </div>
    );
  }

  return (
    <div>
      {/* Sort header */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-100 rounded-t-lg border-b border-gray-200">
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <span className="font-medium">Sort by:</span>
          {SORT_COLUMNS.map((col) => (
            <button
              key={col.field}
              onClick={() => handleSort(col.field)}
              className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                sortConfig.field === col.field
                  ? 'bg-teal-100 text-teal-700'
                  : 'hover:bg-gray-200 text-gray-600'
              }`}
            >
              <span>{col.shortLabel}</span>
              {renderSortIndicator(col.field)}
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-500">
          {technicians.length} tech{technicians.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Technician rows */}
      <div className="bg-white rounded-b-lg">
        {technicians.map((tech, index) => (
          <TechnicianRow
            key={tech.techId}
            technician={tech}
            dailyEntries={dailyByTech[tech.techId] || []}
            isExpanded={expandedIds.has(tech.techId)}
            onToggle={() => toggleExpanded(tech.techId)}
            rank={index + 1}
          />
        ))}
      </div>
    </div>
  );
};
